"use client";

import { ImpactPartner } from "@mw/shared/payload-types";
import { useTranslations } from "next-intl";
import { useCallback, useMemo, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

export type CategoryName = ImpactPartner["category"] | "all";

export const useGetCategoryLabel = () => {
  const t = useTranslations("ClientKeys.Components.impactGrid");
  const tc = useCallback(
    (category: CategoryName) => {
      switch (category) {
        case "healthcare":
          return t("category_healthcare");
        case "education":
          return t("category_education");
        case "culture":
          return t("category_culture");
        case "all":
          return t("category_all");
        default:
          const _exhaustiveCheck: never = category;
          throw new Error(`Unknown impact partner category: ${category}`);
      }
    },
    [t],
  );
  return tc;
};

export const useImpactPartnerSearch = (partners: ImpactPartner[]) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useDebounceValue(searchQuery, 300);

  const handleSearchQueryChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      setDebouncedSearchQuery(value);
    },
    [setSearchQuery, setDebouncedSearchQuery],
  );

  const filteredPartners = useMemo(() => {
    let filtered = partners;

    // Filter by search query
    if (debouncedSearchQuery.trim()) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(p => p.name.toLowerCase().includes(query));
    }

    return filtered;
  }, [partners, debouncedSearchQuery]);

  const partnersByCategory = useMemo(() => {
    const grouped: Partial<Record<ImpactPartner["category"], ImpactPartner[]>> = {};

    filteredPartners.forEach(partner => {
      if (!grouped[partner.category]) {
        grouped[partner.category] = [];
      }
      grouped[partner.category]!.push(partner);
    });

    return grouped;
  }, [filteredPartners]);

  return {
    searchQuery,
    setSearchQuery: handleSearchQueryChange,
    filteredPartners,
    partnersByCategory,
  };
};
